import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["onSelectKnowledgeBase", "selectedKnowledgeBaseId", "onAddKnowledgeBaseClick", "DisabledTooltipTitle", "showAddKnowledgeBaseButton", "showLegacyKnowledgeBases"];
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { isMigrationFailed } from 'knowledge-content-types/KnowledgeBaseV3Service/MigrationTypes';
import { useCallback, useMemo } from 'react';
import { useUpgradeModal } from 'ui-addon-upgrades/ums/useUpgradeModal';
import { useHasScope } from '../../auth/hooks/authHooks';
import { KNOWLEDGE_BASE_WRITE } from '../../constants/ScopesConstants';
import { knowledgeBaseMigrationReportSync } from '../../migration-handler/singleton/migrationHandler';
import { getSettingUrl } from '../../routing/utils/externalUrls';
import { getIsUngatedForKBv3Migration, getIsUngatedForKnowledgeBaseV3_1 } from '../../selectors/gates';
import TourStepWithProgress from '../../tour/components/TourStepWithProgress';
import { KNOWLEDGE_BASE_SWITCHER_STEP } from '../../tour/config/NewUserOnboardingTourConfig';
import { useAppSelector } from '../../utils/hooks';
import { convertSCPToLegacyKnowledgeBase } from '../data/KnowledgeBase';
import { selectHasAccessToMultiKB, selectKnowledgeBaseLimit } from '../selectors/selectHasAccessToMultiKB';
import { Button } from 'foundations-components/transitional/components/Button';
import { Link } from 'foundations-components/transitional/components/Link';
import { Select } from 'foundations-components/transitional/components/Select';
import { Tag } from 'foundations-components/transitional/components/Tag';
import { View } from 'foundations-components/transitional/components/View';
import { Text } from 'foundations-components/components/Text';
import { jsx as _jsx } from "react/jsx-runtime";
const KnowledgeBaseMigrationSelect = _ref => {
  let {
      onSelectKnowledgeBase,
      selectedKnowledgeBaseId,
      onAddKnowledgeBaseClick = () => {},
      DisabledTooltipTitle,
      showAddKnowledgeBaseButton = true,
      showLegacyKnowledgeBases = true
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    hasScope: hasKnowledgeBaseWrite
  } = useHasScope(KNOWLEDGE_BASE_WRITE);
  const {
    new: newKBs,
    legacy,
    migrated
  } = knowledgeBaseMigrationReportSync();
  const hasAccessToMultiKB = useAppSelector(selectHasAccessToMultiKB);
  const knowledgeBaseLimit = useAppSelector(selectKnowledgeBaseLimit);
  const isUngatedForKBv3 = useAppSelector(getIsUngatedForKnowledgeBaseV3_1);
  const isUngatedForKBv3Migration = useAppSelector(getIsUngatedForKBv3Migration);
  const {
    openUpgradeModal
  } = useUpgradeModal('multiple-knowledge-base-creation', !hasAccessToMultiKB);
  const handleSelectKnowledgeBase = useCallback(({
    currentTarget: {
      value: newKnowledgeBaseId
    }
  }) => {
    onSelectKnowledgeBase(parseInt(newKnowledgeBaseId, 10));
  }, [onSelectKnowledgeBase]);
  const knowledgeBasesByTextAndValue = useMemo(() => {
    return [...newKBs.map(({
      knowledgeBase
    }) => ({
      knowledgeBase: convertSCPToLegacyKnowledgeBase(knowledgeBase)
    })), ...migrated.map(({
      scpKnowledgeBase,
      legacyKnowledgeBase,
      migration
    }) => ({
      knowledgeBase: isMigrationFailed(migration.status) && showLegacyKnowledgeBases ? legacyKnowledgeBase : convertSCPToLegacyKnowledgeBase(scpKnowledgeBase)
    })), ...(showLegacyKnowledgeBases ? legacy : [])].map(({
      knowledgeBase: {
        name,
        id,
        isConvertedSCPKnowledgeBase
      }
    }) => ({
      text: name,
      value: id,
      badge: !isConvertedSCPKnowledgeBase && isUngatedForKBv3 && isUngatedForKBv3Migration ? /*#__PURE__*/_jsx(View, {
        marginBlock: "50 !important",
        marginInlineStart: "200",
        children: /*#__PURE__*/_jsx(Tag, {
          inline: true,
          use: "default",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "knowledgeContentUI.knowledgeBaseMigrationSelect.updateAvailable"
          })
        })
      }) : undefined
    }));
  }, [newKBs, migrated, legacy, isUngatedForKBv3, showLegacyKnowledgeBases, isUngatedForKBv3Migration]);
  const defaultSelectValue = useMemo(() => {
    // if found in the dropdown, use the selectedKnowledgeBaseId
    const currentKbInDropdown = knowledgeBasesByTextAndValue.find(kb => kb.value === selectedKnowledgeBaseId);
    if (currentKbInDropdown) return selectedKnowledgeBaseId;

    // if not found in the dropdown, check if selected KB ID is legacy KB ID with a migration
    // if found, use the new KB ID instead to select the correct option in the dropdown
    const newKb = migrated.find(({
      migration
    }) => {
      return migration.legacyKnowledgeBaseId === selectedKnowledgeBaseId;
    });
    if (newKb) return newKb.migration.structuredContentPlatformKnowledgeBaseId;

    // if all else fails, just use the KB ID. This will display that ID in the selected dropdown.
    return selectedKnowledgeBaseId;
  }, [knowledgeBasesByTextAndValue, migrated, selectedKnowledgeBaseId]);
  const hasUsedAllKnowledgeBases = hasReachedLimit(knowledgeBaseLimit, legacy.length, newKBs.length, migrated.length);
  const tooltipText = !hasKnowledgeBaseWrite ? /*#__PURE__*/_jsx(FormattedMessage, {
    message: "knowledgeContentUI.shared.permissions"
  }) : hasAccessToMultiKB ? /*#__PURE__*/_jsx(FormattedJSXMessage, {
    message: "knowledgeContentUI.knowledgeBaseSelect.reachedKnowledgeBaseLimit_jsx",
    elements: {
      UILink: Link
    },
    options: {
      href: getSettingUrl(),
      external: true,
      style: {
        whiteSpace: 'nowrap'
      }
    }
  }) : /*#__PURE__*/_jsx(FormattedJSXMessage, {
    message: "knowledgeContentUI.knowledgeBaseSelect.upgradeForMultipleKnowledgeBasesTooltip_jsx",
    elements: {
      UIButton: Button
    },
    options: {
      onClick: openUpgradeModal,
      use: 'link-on-dark'
    }
  });
  const ItemComponent = useCallback(({
    children,
    option,
    ['data-option-value']: dataOptionValue
  }) => /*#__PURE__*/_jsx(Tooltip, {
    disabled: !option.disabled,
    title: DisabledTooltipTitle && /*#__PURE__*/_jsx(DisabledTooltipTitle, {
      knowledgeBaseId: option.value
    }),
    placement: "left",
    children: /*#__PURE__*/_jsx("div", {
      "data-option-value": dataOptionValue,
      children: children
    })
  }), [DisabledTooltipTitle]);
  return /*#__PURE__*/_jsx(TourStepWithProgress, {
    stepKey: KNOWLEDGE_BASE_SWITCHER_STEP,
    content: {
      header: /*#__PURE__*/_jsx("h4", {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "knowledgeContentUI.onboardingTour.kbSelectStep.title"
        })
      }),
      body: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "knowledgeContentUI.onboardingTour.kbSelectStep.body"
      }),
      footer: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "knowledgeContentUI.onboardingTour.kbSelectStep.buttonText"
      })
    },
    children: /*#__PURE__*/_jsx(Select, Object.assign({
      "data-test-id": "knowledge-base-select"
    }, rest, {
      options: knowledgeBasesByTextAndValue,
      onChange: handleSelectKnowledgeBase,
      value: defaultSelectValue,
      menuWidth: "auto",
      itemComponent: ItemComponent,
      dropdownFooter: showAddKnowledgeBaseButton && /*#__PURE__*/_jsx(Tooltip, {
        title: tooltipText,
        disabled: !hasUsedAllKnowledgeBases && !!hasKnowledgeBaseWrite,
        placement: "right",
        children: /*#__PURE__*/_jsx(Button, {
          disabled: hasUsedAllKnowledgeBases || !hasKnowledgeBaseWrite,
          onClick: onAddKnowledgeBaseClick,
          use: "link",
          children: /*#__PURE__*/_jsx(Text, {
            textDecoration: "underline",
            typography: "heading-50",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "knowledgeContentUI.knowledgeBaseSelect.createFooter"
            })
          })
        })
      })
    }))
  });
};
export default KnowledgeBaseMigrationSelect;
function hasReachedLimit(knowledgeBaseLimit, numberOfLegacyKbs, numberOfNewKbs, numberOfMigratedKbs) {
  if (!knowledgeBaseLimit) return false;
  if (numberOfLegacyKbs === knowledgeBaseLimit && numberOfMigratedKbs === 0 && numberOfNewKbs === 0) return false;
  return numberOfLegacyKbs + numberOfNewKbs + numberOfMigratedKbs >= knowledgeBaseLimit;
}